/**
 * The toast for a failed upload. A presign the server refused for storage
 * reasons — the workspace is out of space, or the file is over the plan's
 * per-file cap — says so in its own localized words, because the generic
 * "Couldn't upload {filename}" hides the one thing the user needs to know.
 * Any other failure keeps the generic per-file copy.
 *
 * Storage refusals are shown ONCE per batch: every file of a refused
 * multi-file drop fails with the same reason within a few milliseconds,
 * and N identical toasts stacked on top of each other say nothing more
 * than one. Each surface's failed tile / Retry / Dismiss is unaffected —
 * this only decides what the toast says.
 */

import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { localizeDomainError } from '@/api/error-codes'
import { useToast } from '@/components/ToastProvider'

/** Presign refusals whose own message beats the generic per-file copy. */
export const STORAGE_UPLOAD_ERROR_CODES: ReadonlySet<string> = new Set([
  'storage_quota_exceeded',
  'file_too_large',
])

/** A repeat of the same storage message inside this window is one batch. */
export const STORAGE_TOAST_BATCH_MS = 5_000

export function storageUploadErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const code = (err.body as { error?: { code?: unknown } } | null | undefined)?.error?.code
  return typeof code === 'string' && STORAGE_UPLOAD_ERROR_CODES.has(code) ? code : null
}

/**
 * Returns `report(err, filename)`. One instance per surface; the batch
 * window is per instance so two surfaces never swallow each other's toast.
 */
export function useUploadFailureToast(): (err: unknown, filename: string) => void {
  const { t } = useTranslation('uploads')
  const toast = useToast()
  const lastStorageToastRef = useRef<{ message: string; at: number } | null>(null)

  return useCallback(
    (err: unknown, filename: string) => {
      const storageMessage =
        storageUploadErrorCode(err) && err instanceof Error ? localizeDomainError(err, t) : null
      if (!storageMessage) {
        toast.error(t('uploadFailed', { filename }))
        return
      }
      const now = Date.now()
      const last = lastStorageToastRef.current
      if (last && last.message === storageMessage && now - last.at < STORAGE_TOAST_BATCH_MS) return
      lastStorageToastRef.current = { message: storageMessage, at: now }
      toast.error(storageMessage)
    },
    [t, toast],
  )
}
